import { excludeArrayFromArray } from "./Functions";

// What each order took and gave back (Ajmal, 2026-11-05), sent by the backend only when the report asks with
// payment_columns=true, so the restaurant app's rows stay as they were.
export const PAYMENT_COLUMNS = ["cash_to_collect", "online_received", "paid_from_wallet", "refunded_to_wallet"];

// franchise_charge holds the weather surge (Extra_charge), so the reports call it that (Ajmal, 2026-10-24).
const COLUMN_LABELS = {
  franchise_charge: "surge",
  cash_to_collect: "amount to collect",
  online_received: "online payment received",
  paid_from_wallet: "paid from wallet",
  refunded_to_wallet: "refunded to wallet",
};

export const columnLabel = (column) => COLUMN_LABELS[column] || column.replaceAll("_", " ");

// Keys browsers saved in localStorage before these columns came from the backend: the online column was worked out
// here as grand total minus collected, which an edited order made wrong.
const RENAMED_COLUMNS = {
  grand_total_minus_franchise_collected: "online_received",
  online_payment_amount: "online_received",
  franchise_collected_amount: "cash_to_collect",
};

const unique = (columns) => [...new Set(columns)];

export const migrateColumnOrder = (saved, defaults) => {
  if (!Array.isArray(saved)) return saved;
  const renamed = unique(saved.map((column) => RENAMED_COLUMNS[column] || column));
  return defaults.reduce((order, column, index) => {
    if (order.includes(column)) return order;
    const before = defaults.slice(0, index).reverse().find((earlier) => order.includes(earlier));
    const at = before ? order.indexOf(before) + 1 : 0;
    return [...order.slice(0, at), column, ...order.slice(at)];
  }, renamed);
};

export const migrateHiddenColumns = (saved) => {
  if (!Array.isArray(saved)) return saved;
  return saved.filter((column) => !(column in RENAMED_COLUMNS));
};

const toAmount = (value) => {
  if (value === null || value === undefined || value === "") return NaN;
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : NaN;
};

const toAmountOrZero = (value) => {
  const amount = toAmount(value);
  return Number.isNaN(amount) ? 0 : amount;
};

export const DELIVERY_CHARGE_WAIVED = "delivery_charge_waived";

export const deliveryChargeDisplay = (row) => {
  const paid = toAmountOrZero(row && row.delivery_charge);
  const waived = toAmountOrZero(row && row[DELIVERY_CHARGE_WAIVED]);
  return { struck: waived > 0 ? (paid + waived).toFixed(2) : null, shown: paid.toFixed(2) };
};

// Real serializer fields the report never offers or shows. They stay here, not in
// the page's column list, so they are always sent in ex_list and stripped from
// any column order a browser saved before they were hidden.
export const HIDDEN_COLUMNS = [
  "in_profit",
  "commission_on_franchise",
  "commission_amount_on_franchise",
  "franchise_net_balance",
  "franchise_collected_amount",
  "refund_amount",
];

export const withoutHiddenColumns = (columns) => {
  if (!Array.isArray(columns)) return columns;
  return columns.filter((column) => !HIDDEN_COLUMNS.includes(column));
};

// The report sends the columns it does NOT want as ex_list, and the backend pops each name off its serializer. The
// waived charge never goes in it: the delivery cell reads it to strike out its own value even when its column is hidden.
export const reportExList = (selectedColumns) => {
  const chosen = excludeArrayFromArray([], selectedColumns || []).filter(
    (column) => column !== DELIVERY_CHARGE_WAIVED
  );
  return unique([...withoutHiddenColumns(chosen), ...HIDDEN_COLUMNS]);
};

export const MONEY_COLUMNS = [
  "amount",
  "total_food_amount",
  "franchise_charge",
  "restaurent_charge",
  "delivery_charge",
  DELIVERY_CHARGE_WAIVED,
  "incentive",
  "delivery_boy_tip",
  "delivery_boy_earn",
  "wallet_amount_used",
  ...PAYMENT_COLUMNS,
  "commission_amount_on_restaurent",
  "amount_to_restaurant",
  "amount_credited",
  "amount_debited",
];

export const isMoneyColumn = (column) => MONEY_COLUMNS.includes(column);

// Percentages the backend copies onto each report row (accounts/logics.py:
// branch.commission, franchise.commission, order.commission_rate, the GST slab).
// extra_commission_rate is rupees despite its name and is deliberately absent.
export const PERCENTAGE_COLUMNS = [
  "extra_commission_perc",
  "branch_gst_perc",
  "commission_on_restaurent",
  "commission_on_franchise",
];

export const isPercentageColumn = (column) => PERCENTAGE_COLUMNS.includes(column);

const UNTOTALLED_COLUMNS = ["order", "user_of_intrest", "delivery_boy"];

export const footerCell = (rows, column) => {
  if (column === "id") return "Total";
  if (UNTOTALLED_COLUMNS.includes(column)) return "--";
  if (!Array.isArray(rows) || rows.length === 0) return "--";

  const sum = rows.reduce((total, row) => total + toAmountOrZero(row && row[column]), 0);
  if (isPercentageColumn(column)) return `${(sum / rows.length).toFixed(2)} %`;

  const hasNumber = rows.some((row) => !Number.isNaN(toAmount(row && row[column])));
  return hasNumber ? `₹ ${sum.toFixed(2)}` : "--";
};
